import AbstractHttpClient from '@/infrastructure/axios/request';
import { logger } from '@/infrastructure/logger';
import type {
  IAdoptIssueAnalysisReq,
  IAdoptIssueAnalysisRsp,
  IBatchConfirmIssueFixReq,
  IBatchConfirmIssueFixRsp,
  IConfirmIssueFixReq,
  IConfirmIssueFixRsp,
  ICreateManualAnalysisReq,
  ICreateManualAnalysisRsp,
  ICreateManualFixReportReq,
  ICreateManualFixReportRsp,
  IForceFixIssueReq,
  IForceFixIssueRsp,
  IGetAutoFixIssueDetailReq,
  IGetAutoFixIssueDetailRsp,
  IGetAutoFixReportReq,
  IGetAutoFixReportRsp,
  IIgnoreIssueReq,
  IIgnoreIssueRsp,
  IListAutoFixIssuesReq,
  IListAutoFixIssuesRsp,
  IListAutoFixReportsReq,
  IListAutoFixReportsRsp,
  IReactivateIssueReq,
  IReactivateIssueRsp,
  IRerunIssueAnalysisReq,
  IRerunIssueAnalysisRsp,
} from '@/proto/auto-fix-report';
import { TrpcService } from '@/service/trpc';

class AutoFixReportServer extends AbstractHttpClient {
  constructor() {
    super({
      serviceName: TrpcService.AutoFixReportService,
    });
  }

  /**
   * 获取某个产品下的自动修复报告列表（报告入口页）
   */
  public async listReports(req: IListAutoFixReportsReq): Promise<IListAutoFixReportsRsp> {
    try {
      return await this.trpcPost<IListAutoFixReportsRsp>('ListAutoFixReports', req);
    } catch (e) {
      logger.error('listReports e:', e);
      return {
        reports: [],
        total: 0,
      };
    }
  }

  /**
   * 获取单个报告的顶部概要、统计卡片
   */
  public async getReport(req: IGetAutoFixReportReq): Promise<IGetAutoFixReportRsp> {
    try {
      return await this.trpcPost<IGetAutoFixReportRsp>('GetAutoFixReport', req);
    } catch (e) {
      logger.error('getReport e:', e);
      return {};
    }
  }

  /**
   * 获取报告下的 Issue 列表（报告详情页表格）
   */
  public async listIssues(req: IListAutoFixIssuesReq): Promise<IListAutoFixIssuesRsp> {
    try {
      return await this.trpcPost<IListAutoFixIssuesRsp>('ListAutoFixIssues', req);
    } catch (e) {
      logger.error('listIssues e:', e);
      return {
        issues: [],
        total: 0,
        stateCounts: [],
      };
    }
  }

  /**
   * 获取单个 Issue 的详情、分析记录、执行、MR、事件时间线
   */
  public async getIssueDetail(req: IGetAutoFixIssueDetailReq): Promise<IGetAutoFixIssueDetailRsp> {
    try {
      return await this.trpcPost<IGetAutoFixIssueDetailRsp>('GetAutoFixIssueDetail', req);
    } catch (e) {
      logger.error('getIssueDetail e:', e);
      return {};
    }
  }

  /**
   * 采纳某条分析记录
   */
  public async adoptAnalysis(req: IAdoptIssueAnalysisReq): Promise<IAdoptIssueAnalysisRsp> {
    try {
      return await this.trpcPost<IAdoptIssueAnalysisRsp>('AdoptIssueAnalysis', req);
    } catch (e) {
      logger.error('adoptAnalysis e:', e);
      return {};
    }
  }

  /**
   * 人工补充分析
   */
  public async createManualAnalysis(req: ICreateManualAnalysisReq): Promise<ICreateManualAnalysisRsp> {
    try {
      return await this.trpcPost<ICreateManualAnalysisRsp>('CreateManualAnalysis', req);
    } catch (e) {
      logger.error('createManualAnalysis e:', e);
      return {};
    }
  }

  /**
   * 创建手动修复报告
   */
  public async createManualFixReport(req: ICreateManualFixReportReq): Promise<ICreateManualFixReportRsp> {
    try {
      return await this.trpcPost<ICreateManualFixReportRsp>('CreateManualFixReport', req);
    } catch (e) {
      logger.error('createManualFixReport e:', e);
      return {};
    }
  }

  /**
   * 重新分析（云端 / 本地）
   */
  public async rerunAnalysis(req: IRerunIssueAnalysisReq): Promise<IRerunIssueAnalysisRsp> {
    try {
      return await this.trpcPost<IRerunIssueAnalysisRsp>('RerunIssueAnalysis', req);
    } catch (e) {
      logger.error('rerunAnalysis e:', e);
      return {};
    }
  }

  /**
   * 确认执行修复（单个 Issue）
   */
  public async confirmFix(req: IConfirmIssueFixReq): Promise<IConfirmIssueFixRsp> {
    try {
      return await this.trpcPost<IConfirmIssueFixRsp>('ConfirmIssueFix', req);
    } catch (e) {
      logger.error('confirmFix e:', e);
      return {};
    }
  }

  /**
   * 批量确认执行修复（报告详情页批量按钮）
   */
  public async batchConfirmFix(req: IBatchConfirmIssueFixReq): Promise<IBatchConfirmIssueFixRsp> {
    try {
      return await this.trpcPost<IBatchConfirmIssueFixRsp>('BatchConfirmIssueFix', req);
    } catch (e) {
      logger.error('batchConfirmFix e:', e);
      return {
        results: [],
      };
    }
  }

  /**
   * 忽略 Issue
   */
  public async ignoreIssue(req: IIgnoreIssueReq): Promise<IIgnoreIssueRsp> {
    try {
      return await this.trpcPost<IIgnoreIssueRsp>('IgnoreIssue', req);
    } catch (e) {
      logger.error('ignoreIssue e:', e);
      return {};
    }
  }

  /**
   * 重新激活已忽略的 Issue
   */
  public async reactivateIssue(req: IReactivateIssueReq): Promise<IReactivateIssueRsp> {
    try {
      return await this.trpcPost<IReactivateIssueRsp>('ReactivateIssue', req);
    } catch (e) {
      logger.error('reactivateIssue e:', e);
      return {};
    }
  }

  /**
   * 强制修复（即使 AI 不推荐）
   */
  public async forceFixIssue(req: IForceFixIssueReq): Promise<IForceFixIssueRsp> {
    try {
      return await this.trpcPost<IForceFixIssueRsp>('ForceFixIssue', req);
    } catch (e) {
      logger.error('forceFixIssue e:', e);
      return {};
    }
  }
}

export const autoFixReportServer = new AutoFixReportServer();
